import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Clock } from 'lucide-react';
import { apiClient } from '@/shared/api/client';
import { RecipeCard } from '@/features/generate/components/RecipeCard';
import type { RecipeRecord } from '@/shared/types';

async function fetchHistory(): Promise<RecipeRecord[]> {
  const response = await apiClient<{ data: RecipeRecord[] }>('/api/mixes/history');
  return response.data;
}

function HistoryRecord({ record }: { record: RecipeRecord }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-2xl border border-black/10 bg-white/50 p-4 dark:border-white/10 dark:bg-slate-900/50">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-400">
              {record.type === 'cocktail' ? 'Коктейль' : 'Кальян'}
            </span>
            <span className="text-xs text-slate-500">{record.model}</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {record.response.length} {record.response.length === 1 ? 'рецепт' : 'рецепта'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {new Date(record.created_at).toLocaleDateString('ru-RU')}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4 border-t border-black/5 pt-4 dark:border-white/5">
          {record.response.map((recipe, index) => (
            <RecipeCard key={recipe.id} recipe={recipe} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

export function HistoryTab(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['history'],
    queryFn: fetchHistory,
  });

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-500">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-pink-500" />
      </div>
    );
  }

  if (data === undefined || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
        <Clock className="h-10 w-10 opacity-50" />
        <p className="text-sm">История генераций пуста</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data.map((record) => (
        <HistoryRecord key={record.id} record={record} />
      ))}
    </div>
  );
}
